import { USPAPICall, USPAPIReturn, TCData, USPData } from '../interfaces';
import { USPrivacyString } from './USPrivacyString';
import { CCPA_LOCATIONS } from '../utils/constants';
import { cookie } from '@turnercode/cdp-cookie';

export const uspString = new USPrivacyString();
/**
 * Internal function to find a frame, if it exists
 *
 * @param {string} locator - Locator string to use to find frame
 * @returns {object} - Child frame object if found, else null
 */
export function findFrame(locator: string): HTMLElement | HTMLIFrameElement {
  let cmpFrame = null;
  let w: Window;

  for (w = window; w; w = w.parent) {
    try {
      if (w.frames && w.frames[locator]) {
        cmpFrame = w;
        break;
      }
    } catch (e) {
      throw new Error(e);
    }
    if (w === window.top) {
      break;
    }
  }
  return cmpFrame;
}

function createIabStub(locatorName: string): void {
  const addFrame = (): boolean => {
    if (!window.frames[locatorName]) {
      if (document.body) {
        const iframe = document.createElement('iframe');

        iframe.style.cssText = 'display:none';
        iframe.name = locatorName;
        document.body.appendChild(iframe);
      } else {
        setTimeout(addFrame, 5);
      }
      return true;
    }
    return false;
  };

  const uspMsgHandler = (event: MessageEvent) => {
    try {
      const msgIsString = typeof event.data === 'string';
      let i: USPAPICall['__uspapiCall'];
      let json: USPAPICall;

      try {
        json = msgIsString ? JSON.parse(event.data) : event.data;
      } catch (e) {
        json = {};
      }
      if (typeof json === 'object' && json !== null && '__uspapiCall' in json) {
        i = json.__uspapiCall;
        window.__uspapi(
          i.command,
          i.version,
          function (retValue: string, success: boolean) {
            const returnMsg: USPAPIReturn = {};

            returnMsg.__uspapiReturn = {
              returnValue: retValue,
              success,
              callId: i.callId,
            };

            if (event.source instanceof Window) {
              event.source.postMessage(msgIsString ? JSON.stringify(returnMsg) : returnMsg, '*');
            }
          },
          i.parameter
        );
      }
    } catch (e) {
      // TODO - log error ... errorLogger('Privacy', '_uspMsgHandler', e);
    }
  };

  const usingUspFrame = findFrame('__uspapiLocator');

  /* CCPA IAB handler must always be defined, even in GDPR regions... */
  if (usingUspFrame === null) {
    /* But only for the top frame */
    addFrame();
    window.__uspapi['msgHandler'] = uspMsgHandler;
    window.addEventListener('message', window.__uspapi['msgHandler'], false);
  }
}

export function initPrivacy(enabled = true, countryCode: string) {
  const api = {
    isPrivacyEnabled(): boolean {
      return enabled;
    },
    getUSPString(): string {
      return uspString.getUSPrivacyString();
    },
    getUSPData(): USPData {
      return {
        version: uspString.getVersion(),
        uspString: uspString.getUSPrivacyString(),
      };
    },
    setUSPString(str: string): boolean {
      return uspString.setUSPrivacyString(str);
    },
    ccpaShareData(): string {
      if (CCPA_LOCATIONS.includes(countryCode)) {
        uspString.setUSPrivacyString('1YNN');
      }
      return uspString.getUSPrivacyString();
    },
    ccpaDoNotShare(): string {
      if (CCPA_LOCATIONS.includes(countryCode)) {
        uspString.setUSPrivacyString('1YYN');
      }
      return uspString.getUSPrivacyString();
    },
  };

  if (enabled) {
    window.__uspapi = function __uspapi(
      command: string,
      version: number,
      callback: (uspData: USPData, success: boolean) => void
    ): void {
      if (command === 'getUSPData' && Number(version) === 1) {
        callback({ version, uspString: uspString.getUSPrivacyString() }, true);
      } else if (command === 'ccpaDoNotShare') {
        callback({ version, uspString: api.ccpaDoNotShare() }, true);
      } else if (command === 'ccpaShareData') {
        callback({ version, uspString: api.ccpaShareData() }, true);
      } else if (command === 'ping') {
        callback({ version, uspapiLoaded: true }, true);
      } else {
        callback(null, false);
      }
    };
    createIabStub('__uspapiLocator');

    const str = cookie.get('usprivacy') as string;

    if (str) {
      uspString.setUSPrivacyString(str);
    } else if (CCPA_LOCATIONS.includes(countryCode)) {
      uspString.setUSPrivacyString('1YNN');
    } else {
      uspString.setUSPrivacyString('1---');
    }

    window.WM = window.WM || {};
    window.WM.PSM = window.WM.PSM || {};
    Object.assign(window.WM?.PSM, api);
  }

  return api;
}

export function checkOutsideUS(brand: string): Promise<{ shouldLoad: boolean; categories: Record<string, boolean>[] }> {
  return new Promise((resolve) => {
    try {
      // IAB config consent categories
      const defaultCategories = {
        '1': 'data-store',
        '2': 'ads-contextual',
        '3': 'ads-person-prof',
        '4': 'ads-person',
        '5': 'content-person-prof',
        '6': 'consent-person',
        '7': 'measure-ads',
        '8': 'measure-content',
        '9': 'measure-market',
        '10': 'product-develop',
      };

      // Adding in brand specific logic for consent category
      const brandCategories = { ...defaultCategories };
      if (brand === 'Bleacher Report') {
        delete brandCategories[5];
        delete brandCategories[6];
        delete brandCategories[8];
      }

      // Checking the consent by prioritizing TCF > UCL > OneTrust
      // Check for TCF API (GDPR/EMEA only)
      if (typeof window?.__tcfapi === 'function') {
        window.__tcfapi('getTCData', 2, (tcData: TCData, success: boolean) => {
          if (success && 'purpose' in tcData && 'legitimateInterests' in tcData.purpose) {
            const combined = {
              ...(tcData.purpose.legitimateInterests || {}),
              ...(tcData.purpose.consents || {}),
            };
            const result: Record<string, boolean>[] = [];
            let shouldLoad = true;

            Object.entries(brandCategories).forEach(([id, name]) => {
              if (combined[id]) {
                result.push({ [name]: combined[id] });
              } else {
                result.push({ [name]: false });
                shouldLoad = false;
              }
            });

            resolve({
              shouldLoad,
              categories: result.concat([
                { 'special-purpose-1': true },
                { 'special-purpose-2': true },
                { 'feature-1': true },
                { 'feature-2': true },
                { 'feature-3': true },
              ]),
            });
          } else {
            resolve({
              shouldLoad: false,
              categories: [],
            });
          }
        });
        return;
      }

      // Check for the User Consent library (UCL)
      if (typeof window?.WBD?.UserConsent?.inUserConsentState === 'function') {
        const supersetConfigCategories = {
          ...brandCategories,
          '11': 'targeting',
          '12': '3p-targeting',
          '13': 'performance',
          '14': 'functional',
        };
        const defaultCategoriesArray = Object.values(supersetConfigCategories);
        const defaultCategoriesConsent = Object.values(supersetConfigCategories).map((category) => ({
          [category]: true,
        }));

        // Checking user consent for all possible consent categories
        const userConsent: boolean = window.WBD.UserConsent.inUserConsentState(defaultCategoriesArray) === true;
        resolve({
          shouldLoad: userConsent,
          categories: userConsent ? defaultCategoriesConsent : [],
        });
        return;
      }

      // Check for OneTrust
      if (typeof window?.OnetrustActiveGroups === 'string') {
        const oneTrustRequiredValuesWM = ['tc', 'fc', 'pf'];
        const oneTrustRequiredValuesWB = ['tc', 'fc', 'pc'];
        const oneTrustRequiredValuesWBD = ['tc', 'ftc', 'pfc'];
        const oneTrustRequiredValuesDiscovery = ['C0002', 'C0003', 'C0004'];
        const oneTrustRequiredCategories = [{ Targeting: true }, { Functional: true }, { Performance: true }];
        const oneTrustFoundValues = window.OnetrustActiveGroups.trim()
          ? window.OnetrustActiveGroups.trim().split(',')
          : [];

        const userConsent =
          oneTrustRequiredValuesWM.every((value) => oneTrustFoundValues.includes(value)) ||
          oneTrustRequiredValuesWB.every((value) => oneTrustFoundValues.includes(value)) ||
          oneTrustRequiredValuesWBD.every((value) => oneTrustFoundValues.includes(value)) ||
          oneTrustRequiredValuesDiscovery.every((value) => oneTrustFoundValues.includes(value));

        resolve({
          shouldLoad: userConsent,
          categories: userConsent ? oneTrustRequiredCategories : [],
        });
        return;
      }
      resolve({
        shouldLoad: false,
        categories: [],
      });
    } catch (error) {
      console.log('[PSM]: Error encountered during consent check', error);
      resolve({
        shouldLoad: false,
        categories: [],
      });
    }
  });
}
